import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { displayMessage } from "$/lib/components/molecules/CustomToast/CustomToast.utils";
import { useAuth } from "$/lib/providers/AuthProvider";
import type {
  Invoice,
  InvoiceListQueryParams,
  InvoiceListResponse,
} from "$/lib/types/models/Invoice.types";
import type { PaymentMethod } from "$/lib/types/models/PaymentMethod.types";
import type {
  BillingPortalPayload,
  BillingPortalResponse,
  CheckoutSessionPayload,
  CheckoutSessionResponse,
  SubscriptionPlan,
  SubscriptionStatusResponse,
  SuccessResponse,
  SyncResult,
} from "$/lib/types/models/Subscription.types";
import { restApiClient } from "$/lib/utils/restApiClient";

const apiEndPoint = "payment";
const subscriptionStatusKey = ["payment-subscription-status"];
const invoicesKey = ["payment-invoices"];
const paymentMethodsKey = ["payment-methods"];
const defaultPaymentMethodKey = ["default-payment-method"];
const subscriptionPlansKey = ["subscription-plans"];

export async function createCheckoutSessionApi(data: CheckoutSessionPayload) {
  return restApiClient.post<CheckoutSessionResponse>(
    `${apiEndPoint}/checkout`,
    { data },
  );
}

export function useCreateCheckoutSession() {
  return useMutation({
    mutationFn: createCheckoutSessionApi,
  });
}

export async function createBillingPortalSessionApi(
  data: BillingPortalPayload,
) {
  return restApiClient.post<BillingPortalResponse>(
    `${apiEndPoint}/billing-portal`,
    { data },
  );
}

export function useCreateBillingPortalSession() {
  return useMutation({
    mutationFn: createBillingPortalSessionApi,
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });
}

export async function fetchSubscriptionStatusApi() {
  return restApiClient.get<SubscriptionStatusResponse>(
    `${apiEndPoint}/subscription`,
  );
}

export function useSubscriptionStatus() {
  return useQuery({
    queryKey: subscriptionStatusKey,
    queryFn: fetchSubscriptionStatusApi,
    refetchOnMount: true,
  });
}

export async function cancelSubscriptionApi() {
  return restApiClient.post<SuccessResponse>(
    `${apiEndPoint}/subscription/cancel`,
  );
}

export function useCancelSubscription() {
  const queryClient = useQueryClient();
  const { invalidateUser } = useAuth();

  return useMutation({
    mutationFn: cancelSubscriptionApi,
    onSuccess: () => {
      queryClient.refetchQueries({
        predicate: (query) => !query.queryKey.includes("syncStatus"),
      });
      displayMessage("Abonnement annulé avec succès.", "success");
      invalidateUser();
    },
  });
}

export async function fetchInvoicesApi(params: InvoiceListQueryParams) {
  const { page } = params;

  return restApiClient.get<InvoiceListResponse>(`${apiEndPoint}/invoices`, {
    query: { page },
  });
}

export async function fetchAllInvoicesApi(): Promise<Invoice[]> {
  let page = 1;
  let hasMore = true;
  const all: Invoice[] = [];

  while (hasMore) {
    const response = await fetchInvoicesApi({ page });
    all.push(...response.invoices);
    page += 1;
    hasMore = page <= response.totalNumberOfPages;
  }

  return all;
}

export function useInvoices(params: InvoiceListQueryParams) {
  return useQuery({
    queryKey: [...invoicesKey, params],
    queryFn: () => fetchInvoicesApi(params),
    refetchOnMount: true,
  });
}

export async function fetchInvoiceByIdApi(invoiceId: number) {
  return restApiClient.get<Invoice>(`${apiEndPoint}/invoices/${invoiceId}`);
}

export function useInvoice(invoiceId: number, enabled = true) {
  return useQuery({
    queryKey: [...invoicesKey, invoiceId],
    queryFn: () => fetchInvoiceByIdApi(invoiceId),
    enabled: Boolean(invoiceId) && enabled,
    refetchOnMount: true,
  });
}

export async function fetchPaymentMethodsApi() {
  return restApiClient.get<PaymentMethod[]>(`${apiEndPoint}/payment-methods`);
}

export function usePaymentMethods() {
  return useQuery({
    queryKey: paymentMethodsKey,
    queryFn: fetchPaymentMethodsApi,
    refetchOnMount: true,
  });
}

export async function fetchDefaultPaymentMethodApi() {
  return restApiClient.get<PaymentMethod>(
    `${apiEndPoint}/payment-methods/default`,
  );
}

export function useDefaultPaymentMethod() {
  return useQuery({
    queryKey: defaultPaymentMethodKey,
    queryFn: fetchDefaultPaymentMethodApi,
    refetchOnMount: true,
  });
}

export async function fetchPaymentMethodByIdApi(paymentMethodId: number) {
  return restApiClient.get<PaymentMethod>(
    `${apiEndPoint}/payment-methods/${paymentMethodId}`,
  );
}

export function usePaymentMethod(paymentMethodId: number, enabled = true) {
  return useQuery({
    queryKey: [...paymentMethodsKey, paymentMethodId],
    queryFn: () => fetchPaymentMethodByIdApi(paymentMethodId),
    enabled: Boolean(paymentMethodId) && enabled,
    refetchOnMount: true,
  });
}

export async function syncPaymentMethodsApi() {
  return restApiClient.post<SyncResult>(`${apiEndPoint}/payment-methods/sync`);
}

export function useSyncPaymentMethods() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncPaymentMethodsApi,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: paymentMethodsKey });
      queryClient.refetchQueries({ queryKey: defaultPaymentMethodKey });
    },
  });
}

export async function setDefaultPaymentMethodApi(paymentMethodId: number) {
  return restApiClient.post<SuccessResponse>(
    `${apiEndPoint}/payment-methods/${paymentMethodId}/default`,
  );
}

export function useSetDefaultPaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: setDefaultPaymentMethodApi,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: paymentMethodsKey });
      queryClient.refetchQueries({ queryKey: defaultPaymentMethodKey });
    },
  });
}

export async function deletePaymentMethodApi(paymentMethodId: number) {
  return restApiClient.delete<SuccessResponse>(
    `${apiEndPoint}/payment-methods/${paymentMethodId}`,
  );
}

export function useDeletePaymentMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deletePaymentMethodApi,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: paymentMethodsKey });
      queryClient.refetchQueries({ queryKey: defaultPaymentMethodKey });
    },
  });
}

export async function fetchSubscriptionPlansApi() {
  return restApiClient.get<SubscriptionPlan[]>(`${apiEndPoint}/plans`);
}

export function useSubscriptionPlans() {
  return useQuery({
    queryKey: subscriptionPlansKey,
    queryFn: fetchSubscriptionPlansApi,
    refetchOnMount: true,
  });
}

export async function syncSubscriptionPlansApi() {
  return restApiClient.post<SyncResult>(`${apiEndPoint}/plans/sync`);
}

export function useSyncSubscriptionPlans() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncSubscriptionPlansApi,
    onSuccess: () => {
      queryClient.refetchQueries({ queryKey: subscriptionPlansKey });
    },
  });
}
